import DOMPurify from 'dompurify';
import {
  ALLOWED_HTML_ATTRIBUTES,
  ALLOWED_HTML_TAGS,
  ALLOWED_INLINE_HTML_TAGS,
  LINE_BREAK_TAGS,
} from './constants';
import { extractTextColor, toTextColorStyle } from './textColor';

const TRAILING_EMPTY_PARAGRAPH = /<p><\/p>\s*$/;
// Enter on an empty list item lifts the cursor out of the list into this
// paragraph; it is the "left the list" state and must survive a round-trip.
const EMPTY_PARAGRAPH_AFTER_LIST = /<\/(ol|ul)>\s*<p><\/p>\s*$/;
/** Any run of line-break tags in sanitized output (attribute-free, so a plain pattern is exact). */
const LINE_BREAK_RUN = /(?:\s*(?:<\/?(?:p|div)>|<br\s*\/?>)\s*)+/g;
const STYLED_OR_SPAN = /<span|\sstyle=/i;

const parseHtml = (html: string): HTMLTemplateElement => {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template;
};

/**
 * A span is kept only to carry a text colour, stored as `color:#RRGGBB`; a
 * span without one is unwrapped and every other inline style is dropped. The
 * backend sanitizes the same way (PT-7251), so what the editor saves and what
 * the API answers compare equal.
 */
const keepOnlyTextColor = (html: string): string => {
  if (!STYLED_OR_SPAN.test(html)) {
    return html;
  }
  const { content } = parseHtml(html);
  content.querySelectorAll('[style]').forEach((element) => {
    if (element.nodeName !== 'SPAN') {
      element.removeAttribute('style');
    }
  });
  content.querySelectorAll('span').forEach((span) => {
    const color = extractTextColor(span.getAttribute('style'));
    if (color) {
      span.setAttribute('style', toTextColorStyle(color));
    } else {
      span.replaceWith(...Array.from(span.childNodes));
    }
  });
  const container = document.createElement('div');
  container.append(content);
  return container.innerHTML;
};

const purify = (html: string, allowedTags: readonly string[]): string =>
  keepOnlyTextColor(
    DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [...allowedTags],
      ALLOWED_ATTR: [...ALLOWED_HTML_ATTRIBUTES],
      KEEP_CONTENT: true,
    })
  );

const removeTrailingEmptyParagraph = (html: string): string => {
  if (EMPTY_PARAGRAPH_AFTER_LIST.test(html)) {
    return html;
  }
  return html.replace(TRAILING_EMPTY_PARAGRAPH, '');
};

/**
 * Sanitizes editor HTML and keeps TipTap's own list shape
 * (`<li><p>…</p></li>` and the `<p></p>` after a list), which the editor needs
 * to keep the cursor in place. Use `toPersistedRichTextHtml` for what is saved.
 */
export const sanitizeRichTextHtml = (html: string): string =>
  removeTrailingEmptyParagraph(purify(html, ALLOWED_HTML_TAGS));

/**
 * Single-line rich text as it is saved and shown (an `OPTION_SELECTION`
 * option label, PT-7250): inline formatting only, the paragraphs and line
 * breaks of an editor read as a space. Same result as the backend's
 * `sanitizeInlineRichText`.
 */
export const toPersistedInlineRichTextHtml = (html: string): string =>
  purify(html, [...ALLOWED_INLINE_HTML_TAGS, ...LINE_BREAK_TAGS])
    .replace(LINE_BREAK_RUN, ' ')
    .trim();

const isParagraph = (node: Node): node is HTMLParagraphElement =>
  node.nodeName === 'P';

const isList = (node: Element | null): boolean =>
  node?.nodeName === 'OL' || node?.nodeName === 'UL';

/**
 * `<li><p>a</p></li>` → `<li>a</li>`. Several paragraphs in one item are
 * joined with `<br>`; nested lists and other children stay as they are.
 */
const unwrapListItemParagraphs = (listItem: HTMLLIElement): void => {
  let previousWasParagraph = false;
  Array.from(listItem.childNodes).forEach((child) => {
    if (!isParagraph(child)) {
      previousWasParagraph = false;
      return;
    }
    if (previousWasParagraph) {
      listItem.insertBefore(listItem.ownerDocument.createElement('br'), child);
    }
    child.replaceWith(...Array.from(child.childNodes));
    previousWasParagraph = true;
  });
};

/**
 * HTML as it is saved and read by mobile and PDF (PT-6785): list items hold
 * their text directly (`<li>text</li>`) and there is no empty paragraph after
 * a trailing list. The editor keeps TipTap's shape while editing.
 */
export const toPersistedRichTextHtml = (html: string): string => {
  const sanitized = sanitizeRichTextHtml(html);
  if (!sanitized.includes('<li')) {
    return sanitized;
  }

  const template = parseHtml(sanitized);
  template.content
    .querySelectorAll('li')
    .forEach((listItem) => unwrapListItemParagraphs(listItem));

  const last = template.content.lastElementChild;
  if (
    last &&
    isParagraph(last) &&
    last.childNodes.length === 0 &&
    isList(last.previousElementSibling)
  ) {
    last.remove();
  }

  return template.innerHTML;
};

/**
 * Comparison key for rich text: the editor's shape and the saved shape of the
 * same content are equal, so our own `onChange` echoed back as `value` does
 * not reset the editor (which would drop the selection).
 */
export const toComparableRichTextHtml = toPersistedRichTextHtml;
